import { useQuery } from '@tanstack/react-query';
import axios from '../api/request';

interface ImpcCodeApiResponse {
  id: number;
  name: string;
  exchangeOfficeName: string;
  exchangeOfficeId: number;
  excludeFromUnex: boolean;
}

interface ImpcCode {
  id: number;
  ppo: string;
  impc: string;
  exclude: string;
}

interface UseImpcCodesParams {
  designatedOperator?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  searchColumn?: string;
  searchTerm?: string;
}

interface ImpcCodesApiResponse {
  content: ImpcCodeApiResponse[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

/**
 * Hook to fetch IMPC codes with pagination support
 * 
 * @param params Object containing:
 *   - designatedOperator: Optional designated operator code (PPO) to filter IMPC codes
 *   - page: Current page number (0-indexed, defaults to 0)
 *   - pageSize: Number of items per page (defaults to 10)
 *   - enabled: Whether the query should execute (defaults to true)
 *   - sortColumn: Column to sort by (defaults to 'name')
 *   - sortDirection: Sort direction (defaults to 'asc')
 * @returns Query result with IMPC codes and pagination metadata
 */
export const useImpcCodes = ({
  designatedOperator,
  page = 0,
  pageSize = 10,
  enabled = true,
  sortColumn = 'name',
  sortDirection = 'ASC',
  searchColumn,
  searchTerm
}: UseImpcCodesParams = {}) => {
  return useQuery<{ data: ImpcCode[]; totalCount: number }>({
    queryKey: ['impcCodes', designatedOperator ?? 'all', page, pageSize, sortColumn, sortDirection, searchColumn, searchTerm],
    queryFn: async () => {
      try {
        // Build params object
        const params: Record<string, any> = {
          page,
          size: pageSize,
          sort: `${sortColumn},${sortDirection.toLowerCase()}`
        };
        
        // Only add designatedOperator param if it's provided
        if (designatedOperator) {
          params.designatedOperator = designatedOperator;
        }
        
        // Add search/filter params if provided
        if (searchColumn && searchTerm) {
          // Map the search column to the API parameter
          if (searchColumn === 'name') {
            params.name = searchTerm;
          } else if (searchColumn === 'exchangeOfficeName') {
            params.designatedOperator = searchTerm;
          } else if (searchColumn === 'excludeFromUnex') {
            params.excludeFromUnex = searchTerm;
          }
        }
        
        // Make a direct request to the /impc endpoint
        const response = await axios.get<ImpcCodesApiResponse>('/impc', {
          params
        });
        
        // Handle paginated response format
        if (response.data && response.data.content && Array.isArray(response.data.content)) {
          // Transform API response to match component's expected format
          const transformedData = response.data.content.map((item: ImpcCodeApiResponse) => ({
            id: item.id,
            ppo: item.exchangeOfficeName || '',
            impc: item.name || '',
            exclude: item.excludeFromUnex ? 'y' : 'n'
          }));
          
          return {
            data: transformedData,
            totalCount: response.data.totalElements || 0
          };
        }
        
        // Fallback for non-paginated response
        if (Array.isArray(response.data)) {
          const rawData = response.data as unknown as ImpcCodeApiResponse[];
          const transformedData = rawData.map((item: ImpcCodeApiResponse) => ({
            id: item.id,
            ppo: item.exchangeOfficeName || '',
            impc: item.name || '',
            exclude: item.excludeFromUnex ? 'y' : 'n'
          }));
          
          return {
            data: transformedData,
            totalCount: transformedData.length
          };
        }
        
        return { data: [], totalCount: 0 };
      } catch (error) {
        console.error('Error fetching IMPC codes:', error);
        return { data: [], totalCount: 0 };
      }
    },
    enabled,
    staleTime: 0, // Always consider data stale to ensure fresh fetch
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true // Always refetch when component mounts
  });
}; 